import { Doc } from "@/components/documents/types";
import { MediaList } from "@/components/media/media-list";
import { PageHeader } from "@/components/layouts/page-header";
import { FileUploadForm } from "@/components/documents/file-upload-form";
import { generateColumn, generateDateColumn } from "@/components/table/table-cell-renderers";
import { useDocuments, useUploadDocument } from "@/hooks/use-documents";
import { CustomerMedia, useMedias } from "@/hooks/use-medias";
import { useFileUpload } from "@/hooks/use-file-upload";
import { Card, CardBody, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { Skeleton } from "@/components/ui/skeleton";
import { Body } from "@/components/ui/text";
import { useMobileSize } from "@repo/common/hooks/use-mobile-size";
import { ColumnDef } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { formatDate } from "@repo/common/utils/format-date";
import { useAddCustomerMedia, useRemoveCustomerMedia } from "@/hooks/use-media-mutations";
import { queryClient } from "@/lib/tanstack/query-client";
import { toast } from "@/components/ui/sonner";
import { useCompany } from "@/hooks/use-company";
const upcomingVisitColumns: ColumnDef<Doc>[] = [generateDateColumn<Doc>({
  key: "date",
  header: "Date"
}), generateColumn<Doc>({
  key: "name",
  header: "Document Name"
}), generateColumn<Doc>({
  key: "id",
  header: () => null,
  cell: value => {
    return <div className="flex gap-3 items-center justify-end cursor-pointer">
          <div tabIndex={0} className="text-sm text-blue-500 opacity-0 group-hover/row:[&:not(:has(~:hover))]:opacity-100 group-focus/row:opacity-100 transition-opacity">
            View
          </div>
          <Link href={`/docs/view/${value}/`} className="cursor-pointer">
            <FileText className="w-5 h-5" />
          </Link>
        </div>;
  }
})];
export default function DocsPage() {
  const {
    data: documents,
    isLoading
  } = useDocuments();
  const {
    data: medias,
    isLoading: isMediaLoading
  } = useMedias();
  const {
    data: company
  } = useCompany();
  const {
    mutate: addCustomerMedia
  } = useAddCustomerMedia();
  const isMobile = useMobileSize();
  const router = useRouter();
  const {
    mutateAsync: uploadDocumentMutation
  } = useUploadDocument();
  const {
    mutate: removeCustomerMedia
  } = useRemoveCustomerMedia({
    onSuccess: () => {
      toast("Media removed successfully");
    }
  });
  const {
    uploadFile
  } = useFileUpload({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["customer-medias"]
      });
    },
    onError: (file, error) => {
      console.error("File upload failed:", file.name, error);
    },
    resizeImage: {
      maxDimension: 1000,
      quality: 0.7
    }
  });
  const handleRowClick = useCallback((row: Doc) => {
    router.push(`/docs/view/${row.id}/`);
  }, [router]);
  const handleRemoveMedia = useCallback((media: CustomerMedia) => {
    removeCustomerMedia({
      mediaId: media.id
    });
  }, [removeCustomerMedia]);
  const handleFileUpload = useCallback(async (file_0: any) => {
    try {
      // get signed URL from GraphQL mutation
      const uploadData = await uploadDocumentMutation({
        contentType: file_0.file.type,
        size: file_0.file.size
      });
      // upload file to s3 using signed URL
      const uploadedResponse = await uploadFile(file_0.file,
      // pass the original File object, not the wrapper
      uploadData.uploadUrl, uploadData.contentType);
      if (uploadedResponse && uploadedResponse.success) {
        addCustomerMedia({
          tempFileName: uploadData.key,
          contentType: uploadData.contentType
        });
      }
    } catch (error_0) {
      console.error("Upload failed:", error_0);
    }
  }, [uploadFile, uploadDocumentMutation, addCustomerMedia]);
  return <PageHeader title="Documents">
      <div className="flex flex-col gap-6">
        <Card className="flex flex-col gap-3">
          <CardHeader>
            <CardTitle>Documents</CardTitle>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            {isLoading ? <div className="flex flex-col gap-3">
                <Skeleton className="w-full h-10" />
                <Skeleton className="w-full h-10" />
                <Skeleton className="w-full h-10" />
              </div> : documents && documents.length > 0 ? <div className="flex flex-col gap-3">
                <div className="hidden md:block">
                  <DataTable columns={upcomingVisitColumns} data={documents ?? []} onRowClick={handleRowClick} />
                </div>
                <div className="flex flex-col gap-3 md:hidden">
                  <div className="flex flex-col gap-3">
                    {documents?.map(document => <div key={document.id} className="flex justify-between gap-3 items-center pb-3 border-b border-gray-200 last:border-b-0 cursor-pointer" onClick={() => handleRowClick(document)}>
                        <div className="flex flex-col gap-1">
                          {document.name}
                          <Body>
                            {formatDate({
                        date: document.date,
                        formatString: "MM/dd/yy",
                        timezone: "UTC"
                      })}
                          </Body>
                        </div>
                        <FileText className="w-5 h-5" />
                      </div>)}
                  </div>
                </div>
              </div> : <div className="flex flex-col gap-3 items-center justify-center">
                <FileText className="w-10 h-10" />
                <div>No documents to display</div>
              </div>}
          </CardBody>
        </Card>

        <Card className="flex flex-col gap-3">
          <CardHeader>
            <CardTitle>{`Uploaded Files (${medias?.length})`}</CardTitle>
          </CardHeader>
          <CardContent className="px-0">
            <div className="flex flex-col gap-3">
              {isMediaLoading ? <div className="flex flex-wrap gap-3">
                  <Skeleton className="w-16 h-16" />
                  <Skeleton className="w-16 h-16" />
                  <Skeleton className="w-16 h-16" />
                </div> : medias && medias.length > 0 ? <MediaList media={medias || []} companyName={company?.name} pdfVariant="thumbnail" direction={isMobile ? "vertical" : "horizontal"} onRemove={handleRemoveMedia} /> : <div>No files uploaded</div>}

              <FileUploadForm onFileUpload={handleFileUpload} maxFiles={5} />
            </div>
          </CardContent>
        </Card>
      </div>
    </PageHeader>;
}